import { Box, Paper, styled } from '@mui/material'
import clsx from 'clsx'
import { forwardRef } from 'react'
import { PreviewCardTitle, PreviewCardSubtitle } from './PreviewCardTitle'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

export type PreviewCardProps = {
  header?: ReactNode
  content?: ReactNode
  footer?: ReactNode
  'data-testid'?: string
}

const SECTION_PADDING_FACTOR = 4
const SECTION_CLASS = 'OmuiPreviewCard-section'

const PreviewCardRoot = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 320,
  maxWidth: 450,
  // In a popover, inherit its viewport limit and scroll the surface without clipping its shadow.
  maxHeight: 'inherit',
  overflowX: 'hidden',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  textAlign: 'left',
  borderRadius: theme.shape['radius-xl'],
  boxShadow: theme.customShadows.sm,
}))

// Section presence is decided by the DOM, not by inspecting the ReactNode: a slot whose
// component renders nothing yields an empty div that `:empty` hides, while falsy-but-renderable
// content like `0` produces a text node and stays visible. Two gotchas the selectors encode:
// `:empty` is defeated by whitespace-only text (don't render a bare `{' '}` into a slot), and
// `display: none` siblings still match `~`, so the separator rule needs `:not(:empty)` on both
// sides of the sibling selector or a hidden first section would leave a border at the top of the card.
const PreviewCardSectionRoot = styled('div')(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(SECTION_PADDING_FACTOR),
  '&:empty': {
    display: 'none',
  },
  // Match preceding sections regardless of their generated sx class; & targets this section.
  [`.${SECTION_CLASS}:not(:empty) ~ &:not(:empty)`]: {
    borderTop: `${theme.strokeWidth.default} solid ${theme.palette.border.strong}`,
  },
}))

const PreviewCardSection = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof PreviewCardSectionRoot>>(
  ({ className, ...props }, ref) => <PreviewCardSectionRoot {...props} ref={ref} className={clsx(SECTION_CLASS, className)} />
)
PreviewCardSection.displayName = 'PreviewCardSection'

export const PreviewCard = ({ header, content, footer, 'data-testid': dataTestId }: PreviewCardProps) => (
  <PreviewCardRoot data-testid={dataTestId}>
    <PreviewCardSection>{header}</PreviewCardSection>
    <PreviewCardSection>{content}</PreviewCardSection>
    <PreviewCardSection>{footer}</PreviewCardSection>
  </PreviewCardRoot>
)

type ScrollableBodyProps = {
  children: ReactNode
  /** Caps the body, excluding section padding. The frame's inherited limit remains a backstop. */
  maxHeight?: number | string
}

/** A shrinkable section whose body scrolls while sibling sections remain visible. */
const ScrollableBody = ({ children, maxHeight }: ScrollableBodyProps) => (
  <PreviewCardSection sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flexShrink: 1, p: 0 }}>
    <Box
      // A capped body scrolls, so it needs a tab stop for keyboard users to reach the overflow.
      tabIndex={maxHeight === undefined ? undefined : 0}
      sx={(theme) => ({
        // Padding scrolls with the content; include it in the cap to preserve the body's usable height.
        maxHeight:
          maxHeight === undefined
            ? undefined
            : `calc(${typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight} + ${theme.spacing(SECTION_PADDING_FACTOR * 2)})`,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
      })}
    >
      <Box sx={{ p: SECTION_PADDING_FACTOR }}>{children}</Box>
    </Box>
  </PreviewCardSection>
)

PreviewCard.Frame = PreviewCardRoot
PreviewCard.Section = PreviewCardSection
PreviewCard.ScrollableBody = ScrollableBody
PreviewCard.Title = PreviewCardTitle
PreviewCard.Subtitle = PreviewCardSubtitle
